/**
 * Admin servizi API client — F-009.6 (catalogo CRUD).
 *
 *  - GET    /api/v1/admin/servizi        → { items: ServizioView[] }
 *  - POST   /api/v1/admin/servizi        → ServizioView
 *  - PATCH  /api/v1/admin/servizi/:id    → ServizioView
 *  - DELETE /api/v1/admin/servizi/:id    → 204
 *
 * Convenzioni come `admin-wallet/lib/admin-client.ts`. Prezzi BigInt → stringa;
 * input in euro.
 */
const API = '/api/v1/admin/servizi';

export interface ServizioView {
  id: string;
  nome: string;
  descrizione: string | null;
  prezzoCents: string;
  attivo: boolean;
  updatedAt: string | null;
}

export interface ServiziListResponse {
  items: ServizioView[];
}

export class ServiziApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ServiziApiError';
    this.status = status;
    this.code = code;
  }
}

const ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Dati non validi. Controlla i campi.',
  SERVIZIO_VALIDATION: 'Dati non validi.',
  SERVIZIO_NOT_FOUND: 'Servizio non trovato.',
  FORBIDDEN: 'Accesso negato.',
  UNAUTHORIZED: "Sessione scaduta. Effettua di nuovo l'accesso.",
};

export function errorMessageFor(err: unknown): string {
  if (err instanceof ServiziApiError) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  if (err instanceof TypeError) return 'Errore di connessione. Riprova.';
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function throwForError(res: Response, fallbackCode: string, fallbackMsg: string): Promise<never> {
  const env = (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
  throw new ServiziApiError(res.status, env.code ?? fallbackCode, env.message ?? fallbackMsg);
}

export async function getServizi(): Promise<ServiziListResponse> {
  const res = await fetch(API, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'LIST_FAILED', 'Impossibile caricare i servizi');
  return (await res.json()) as ServiziListResponse;
}

export interface CreateServizioBody {
  nome: string;
  descrizione?: string;
  prezzoEuro: number;
}

export async function createServizio(body: CreateServizioBody): Promise<ServizioView> {
  const res = await fetch(API, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwForError(res, 'CREATE_FAILED', 'Impossibile creare il servizio');
  return (await res.json()) as ServizioView;
}

export interface UpdateServizioBody {
  nome?: string;
  descrizione?: string | null;
  prezzoEuro?: number;
  attivo?: boolean;
}

export async function updateServizio(id: string, body: UpdateServizioBody): Promise<ServizioView> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwForError(res, 'UPDATE_FAILED', 'Impossibile aggiornare il servizio');
  return (await res.json()) as ServizioView;
}

export async function deleteServizio(id: string): Promise<void> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) {
    await throwForError(res, 'DELETE_FAILED', 'Impossibile eliminare il servizio');
  }
}
